// packages/character-render/src/proceduralHumanoid.ts
//
// Hat geometry for the procedural humanoid: pushes the boxes of one hat style
// onto the part list. Split out of buildProceduralHumanoid.
import { METAL, type V3, box } from './humanoidBoxes';
import type { HumanoidPart } from './humanoidRig';
import type { HatStyle, HumanoidPalette } from './humanoidSpec';

export function pushHat(
  parts: HumanoidPart[],
  style: HatStyle,
  p: HumanoidPalette,
  sv: (v: V3) => V3,
): void {
  const hat = p.hat ?? p.accent;
  switch (style) {
    case 'tricorn':
      parts.push(
        box('misc', hat, sv([0.37, 0.035, 0.32]), sv([0, 1.012, 0]), {
          bone: 'head',
          roughness: 0.8,
        }),
      );
      parts.push(
        box('misc', p.accent, sv([0.262, 0.022, 0.242]), sv([0, 1.04, 0]), {
          bone: 'head',
          ...METAL,
        }),
      );
      parts.push(
        box('misc', hat, sv([0.25, 0.085, 0.23]), sv([0, 1.07, 0]), {
          bone: 'head',
          roughness: 0.8,
        }),
      );
      break;
    case 'bandana':
      parts.push(
        box('misc', hat, sv([0.258, 0.075, 0.245]), sv([0, 0.99, 0]), {
          bone: 'head',
          roughness: 0.9,
        }),
      );
      parts.push(
        box('misc', hat, sv([0.06, 0.07, 0.05]), sv([0.04, 0.95, -0.135]), {
          bone: 'head',
          roughness: 0.9,
        }),
      );
      break;
    case 'turban':
      parts.push(
        box('misc', hat, sv([0.29, 0.13, 0.28]), sv([0, 1.03, 0]), {
          bone: 'head',
          roughness: 0.92,
        }),
      );
      parts.push(
        box('misc', p.accent, sv([0.045, 0.045, 0.02]), sv([0, 1.03, 0.145]), {
          bone: 'head',
          ...METAL,
        }),
      );
      break;
    case 'helmet':
      parts.push(
        box('misc', '#9aa2ab', sv([0.275, 0.1, 0.265]), sv([0, 1.0, 0]), {
          bone: 'head',
          ...METAL,
        }),
      );
      parts.push(
        box('misc', '#9aa2ab', sv([0.33, 0.02, 0.31]), sv([0, 0.955, 0]), {
          bone: 'head',
          ...METAL,
        }),
      );
      parts.push(
        box('misc', p.accent, sv([0.04, 0.07, 0.25]), sv([0, 1.075, 0]), {
          bone: 'head',
          roughness: 0.7,
        }),
      );
      break;
    case 'straw':
      parts.push(
        box('misc', hat, sv([0.44, 0.025, 0.44]), sv([0, 1.005, 0]), {
          bone: 'head',
          roughness: 0.95,
        }),
      );
      parts.push(
        box('misc', hat, sv([0.22, 0.08, 0.22]), sv([0, 1.055, 0]), {
          bone: 'head',
          roughness: 0.95,
        }),
      );
      parts.push(
        box('misc', p.accent, sv([0.225, 0.02, 0.225]), sv([0, 1.025, 0]), {
          bone: 'head',
          roughness: 0.9,
        }),
      );
      break;
    case 'conical':
      parts.push(
        box('misc', hat, sv([0.46, 0.03, 0.46]), sv([0, 1.0, 0]), {
          bone: 'head',
          roughness: 0.95,
        }),
      );
      parts.push(
        box('misc', hat, sv([0.3, 0.05, 0.3]), sv([0, 1.04, 0]), { bone: 'head', roughness: 0.95 }),
      );
      parts.push(
        box('misc', hat, sv([0.16, 0.05, 0.16]), sv([0, 1.09, 0]), {
          bone: 'head',
          roughness: 0.95,
        }),
      );
      break;
    case 'cap':
      parts.push(
        box('misc', hat, sv([0.26, 0.065, 0.25]), sv([0, 1.0, 0]), {
          bone: 'head',
          roughness: 0.9,
        }),
      );
      parts.push(
        box('misc', hat, sv([0.2, 0.02, 0.09]), sv([0, 0.975, 0.155]), {
          bone: 'head',
          roughness: 0.9,
        }),
      );
      break;
    default:
      break;
  }
}
